import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { z } from "zod";
import { SiteShell } from "@/components/site/SiteShell";
import { alternatePath, SITE_URL, type Locale } from "@/lib/i18n";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { publicResourcesQuery } from "@/lib/query-registry";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import { ResourcesIndex } from "@/components/resources/ResourcesIndex";
import { withStaticResources } from "@/content/static-resources";
import type { EditorialResource } from "@/components/resources/ResourceEditorialPrimitives";

const resourcesSearchSchema = z.object({
  q: z.string().catch("").default(""),
  cat: z
    .enum(["all", "performance", "conversion", "technical-seo", "migration", "shopify-development"])
    .catch("all")
    .default("all"),
});

function buildItemListJsonLd(resources: any[], locale: Locale) {
  const isEs = locale === "es";
  const basePath = isEs ? "/es/recursos" : "/resources";
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: isEs ? "Recursos Shugert" : "Shugert Resources",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: resources.length,
    itemListElement: resources.map((r: any, i: number) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}${basePath}/${isEs ? r.slug_es ?? r.slug : r.slug}`,
      name: (isEs ? r.title_es || r.title_en : r.title_en) ?? r.slug,
    })),
  };
}

export const Route = createFileRoute("/resources/")({
  validateSearch: resourcesSearchSchema,
  search: { middlewares: [stripSearchParams({ q: "", cat: "all" })] },
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    const data = await context.queryClient.ensureQueryData(publicResourcesQuery());
    return {
      ...data,
      resources: withStaticResources(((data?.resources ?? []) as EditorialResource[])),
    };
  },
  head: ({ loaderData }) => {
    const resources = (loaderData as any)?.resources ?? [];
    return buildHead({
      locale: "en",
      title: "Shopify Resources & Playbooks · Shugert",
      description:
        "Field-tested Shopify guides: performance, CRO, technical SEO, migrations and development, from the team that ships them.",
      path: "/resources",
      altPath: alternatePath("/resources", "es"),
      feeds: [{ href: "/resources/rss.xml", title: "Shugert Resources" }],
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
        ]),
        buildItemListJsonLd(resources, "en"),
      ],
    });
  },
  component: ResourcesRoute,
});

function ResourcesRoute() {
  const data = Route.useLoaderData();
  return (
    <SiteShell>
      <ResourcesIndex locale="en" resources={data?.resources ?? []} />
    </SiteShell>
  );
}